import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Subject, debounceTime, finalize, map, of, switchMap, takeUntil, tap, catchError } from 'rxjs';
import { BasicResult } from 'src/app/core-module/models/basic-result';
import { AuditLocation } from 'src/app/core-module/models/Locations/audit-location';
import { FeaturesList } from 'src/app/core-module/models/Locations/features-list';
import { NewUserResult } from 'src/app/core-module/models/UserManagement/new-user-result';
import { PermissionsUser } from 'src/app/core-module/models/UserManagement/permissions-user';
import { SaveUser } from 'src/app/core-module/models/UserManagement/save-user';
import { UserManagementResult } from 'src/app/core-module/models/UserManagement/user-management-result';
import { UserPermissionsRequest } from 'src/app/core-module/models/UserManagement/user-permissions-request';
import { UserPermissionsLocationListRequest } from 'src/app/core-module/models/UserManagement/user-permissions-location-list-request';
import { UserAudit } from 'src/app/core-module/models/UserManagement/user-audit';
import { UserAuditDetail } from 'src/app/core-module/models/UserManagement/user-audit-detail';
import { UserAuditResult } from 'src/app/core-module/models/UserManagement/user-audit-result';
import { UserLocationLocationSave } from 'src/app/core-module/models/UserManagement/user-location-location-save';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { UserManagementService } from 'src/app/core-module/services/usermanagement.service';

@Component({
  selector: 'app-new-user-wizard',
  templateUrl: './new-user-wizard.component.html',
  styleUrl: './new-user-wizard.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NewUserWizardComponent implements OnInit, OnDestroy {
  @Input() editMode: boolean = false;
  @Input() initialUser: SaveUser | null = null;
  @Input() readOnlyMode: boolean = false;
  @Input() tabMode: boolean = false;
  @Input() activeStep: number | null = null;
  @Input() hideHeader: boolean = false;
  @Output() userDetailsSaved = new EventEmitter<SaveUser>();

  public currentStep: number = 1;
  public isSavingUser: boolean = false;
  public isSavingLocations: boolean = false;
  public isSavingPermissions: boolean = false;

  public invalidEmail: boolean = false;
  public emailUsed: boolean = false;
  public invalidUserName: boolean = false;
  public invalidDisplayName: boolean = false;
  public minUserLength: number = 6;
  public noLicense: boolean = false;
  public checkingLicense: boolean = false;
  public reactivationLicenseUnavailable: boolean = false;
  public isUsernameInUse: boolean = false;

  public newUser: SaveUser = new SaveUser({ UserTypeID: 1 });
  public userDetailsDraft: SaveUser = new SaveUser({ UserTypeID: 1 });
  public createdUser: SaveUser | null = null;

  public originalLocations: AuditLocation[] = [];
  public possibleLocations: AuditLocation[] = [];
  public locationsReady: boolean = false;
  public selectedLocation: AuditLocation = new AuditLocation();
  public isLocationSelected: boolean = false;
  public includeSubLocations: boolean = false;
  public selectedLocationScopes: { LocationID: number; LocationName: string; IncludeSubLocations: boolean; LocationLevelSeq: number }[] = [];
  public minSeq: number = 0;
  public minLevelID: number = 0;
  public topLevel: boolean = true;

  public permissionsReady: boolean = false;
  public newUserLocations: AuditLocation[] = [];
  public mirrorUsers: PermissionsUser[] = [];
  public mirrorUserId: number | null = null;
  public useAdvancedPermissions: boolean = false;
  public mobileSelectedLocationId: number | null = null;

  public auditLoading: boolean = false;
  public auditLoaded: boolean = false;
  public allAuditPermissions: boolean = false;
  public userAudits: UserAudit = new UserAudit();
  private originalUserAudits: UserAudit = new UserAudit();
  public useAuditTypeSelection: boolean = false;
  public auditTypes: { AuditTypeID: number; AuditType: string }[] = [];
  private pendingEditUser: SaveUser | null = null;
  public nonEditableLocationIds: Set<number> = new Set<number>();
  private detailsDirty: boolean = false;
  private locationsDirty: boolean = false;
  private permissionsDirty: boolean = false;
  private auditsDirty: boolean = false;
  private isLoadingLocationsForEdit: boolean = false;
  private isLoadingPermissionsForEdit: boolean = false;
  private destroy$ = new Subject<void>();
  private detailsSave$ = new Subject<void>();
  private locationsSave$ = new Subject<void>();
  private permissionsSave$ = new Subject<void>();
  private auditsSave$ = new Subject<void>();
  private externalChangeDetected: boolean = false;
  private locationsInitialized: boolean = false;

  constructor(
    private router: Router,
    private UMS: UserManagementService,
    private toast: ToastrService
  ) {
    this.loadLocations();
  }

  ngOnInit() {
    if (!this.editMode) {
      this.checkLicenseAvailability();
    }
    this.detailsSave$
      .pipe(debounceTime(800), takeUntil(this.destroy$))
      .subscribe(() => this.trySaveDetails());
    this.locationsSave$
      .pipe(debounceTime(800), takeUntil(this.destroy$))
      .subscribe(() => this.trySaveLocations());
    this.permissionsSave$
      .pipe(debounceTime(800), takeUntil(this.destroy$))
      .subscribe(() => this.trySavePermissions());
    this.auditsSave$
      .pipe(debounceTime(800), takeUntil(this.destroy$))
      .subscribe(() => this.trySaveAudits());
  }

  private checkLicenseAvailability() {
    this.checkingLicense = true;
    this.UMS.hasAvailableUserLicense()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.checkingLicense = false)
      )
      .subscribe({
        next: available => this.noLicense = !available,
        error: () => {
          this.noLicense = false;
          this.toast.addToastr(new Toastr({ Type: 3, Message: 'Unable to check user licence availability. Availability will be checked again when the user is saved.' }));
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get displayStep(): number {
    if (this.tabMode && this.activeStep) {
      return this.activeStep;
    }
    return this.currentStep;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialUser'] && this.initialUser) {
      this.pendingEditUser = this.initialUser;
      this.loadEditUser(this.initialUser);
    }
  }

  loadLocations() {
    this.UMS.getUserManagementLocations()
      .pipe(
        tap((locations: AuditLocation[]) => {
          this.originalLocations = locations;
          this.setMinLevel();
          this.initialList();
          if (this.editMode && this.createdUser) {
            this.loadExistingLocationsForEdit(this.createdUser.UserID);
          } else if (this.editMode && this.pendingEditUser) {
            this.loadExistingLocationsForEdit(this.pendingEditUser.UserID);
          }
        })
      )
      .subscribe();
  }

  private loadEditUser(user: SaveUser) {
    this.createdUser = new SaveUser(user);
    this.newUser = new SaveUser(user);
    this.userDetailsDraft = new SaveUser(user);
    this.resetFlags();
    this.noLicense = false;
    this.reactivationLicenseUnavailable = false;
    this.currentStep = 1;
    this.detailsDirty = false;
    this.locationsDirty = false;
    this.permissionsDirty = false;
    this.auditsDirty = false;
    this.externalChangeDetected = false;
    this.locationsInitialized = false;
    this.isLoadingLocationsForEdit = false;
    this.isLoadingPermissionsForEdit = false;
    if (this.originalLocations.length > 0) {
      this.loadExistingLocationsForEdit(user.UserID);
    }
    this.mirrorUsers = [];
    this.mirrorUserId = null;
    if (!user.Active && user.Email) {
      this.checkManagedUserEmailConflict(true);
    }
  }

  private loadExistingLocationsForEdit(userId: number) {
    if (!userId || userId <= 0) {
      return;
    }
    this.isLoadingLocationsForEdit = true;
    this.UMS.getUserLocationLocations(userId)
      .pipe(
        tap((locations: AuditLocation[]) => {
          const assigned = locations.filter(l => l.HasPermission);
          this.selectedLocationScopes = assigned.map(l => ({
            LocationID: l.LocationID,
            LocationName: l.LocationName,
            IncludeSubLocations: false,
            LocationLevelSeq: l.LocationLevelSeq
          }));
          this.buildLocationScope();
          this.loadExistingPermissionsForEdit(userId);
          this.locationsInitialized = true;
          this.externalChangeDetected = false;
        }),
        finalize(() => {
          this.isLoadingLocationsForEdit = false;
        })
      )
      .subscribe();
  }

  private loadExistingPermissionsForEdit(userId: number) {
    const locationIds = this.getSelectedLocationIds();
    if (locationIds.length === 0) {
      this.mirrorUsers = [];
      this.mirrorUserId = null;
      this.permissionsReady = true;
      this.isLoadingPermissionsForEdit = false;
      return;
    }
    this.isLoadingPermissionsForEdit = true;
    this.permissionsReady = false;
    const request = new UserPermissionsLocationListRequest({
      IsGroup: false,
      LocationIDs: locationIds
    });
    this.UMS.getUserLocationLocations(userId)
      .pipe(
        tap((locations: AuditLocation[]) => {
          this.newUserLocations = locations.map(l => new AuditLocation(l));
          if (this.newUserLocations.length === 0) {
            this.ensureLocationsForPermissionsDisplay();
          }
        }),
        switchMap(() => this.UMS.userManagementListByLocations(request)),
        tap((result: UserManagementResult) => {
          this.setMirrorUsers(result);
          const target = result.Users.find(u => u.UserID === userId);
          if (target && target.Locations.length > 0) {
            const featuresByLocation = new Map<number, AuditLocation>();
            target.Locations.forEach(l => {
              featuresByLocation.set(l.LocationID, new AuditLocation(l));
            });
            this.newUserLocations = this.newUserLocations.map(loc => {
              const match = featuresByLocation.get(loc.LocationID);
              if (match) {
                loc.Features = new FeaturesList(match.Features);
                loc.SubLocationFeatures = new FeaturesList(match.SubLocationFeatures);
              }
              return loc;
            });
            this.syncSimpleFromAdvancedAll();
          }
          if (this.newUserLocations.length === 0) {
            this.ensureLocationsForPermissionsDisplay();
          }
          this.appendReadOnlyLocations(locationIds);
          this.permissionsReady = true;
          this.externalChangeDetected = false;
        }),
        finalize(() => {
          this.isLoadingPermissionsForEdit = false;
        })
      )
      .subscribe();
  }

  private appendReadOnlyLocations(locationIds: number[]) {
    this.nonEditableLocationIds = new Set<number>();
    const existingIds = new Set(this.newUserLocations.map(l => l.LocationID));
    const missing = this.selectedLocationScopes.filter(scope => !existingIds.has(scope.LocationID));
    missing.forEach(scope => {
      this.nonEditableLocationIds.add(scope.LocationID);
      this.newUserLocations.push(new AuditLocation({
        LocationID: scope.LocationID,
        LocationName: scope.LocationName,
        LocationLevelSeq: scope.LocationLevelSeq,
        HasPermission: false
      }));
    });
  }

  private ensureLocationsForPermissionsDisplay() {
    if (this.newUserLocations.length > 0) {
      return;
    }
    this.newUserLocations = this.selectedLocationScopes.map(scope => new AuditLocation({
      LocationID: scope.LocationID,
      LocationName: scope.LocationName,
      LocationLevelSeq: scope.LocationLevelSeq,
      HasPermission: true
    }));
  }

  setMinLevel() {
    if (this.originalLocations.length === 0) {
      return;
    }
    this.minSeq = this.originalLocations[0].LocationLevelSeq;
    this.minLevelID = this.originalLocations[0].LocationLevelID;
    this.originalLocations.forEach((location) => {
      if (location.LocationLevelSeq < this.minSeq) {
        this.minSeq = location.LocationLevelSeq;
        this.minLevelID = location.LocationLevelID;
      }
    });
  }

  initialList() {
    this.possibleLocations = [];
    this.topLevel = true;
    this.originalLocations.forEach((location) => {
      if (location.LocationLevelID === this.minLevelID) {
        this.possibleLocations.push(new AuditLocation(location));
      }
    });
    this.locationsReady = true;
  }

  selectLocation(location: AuditLocation) {
    if (this.readOnlyMode) {
      return;
    }
    if (!location.HasPermission) {
      return;
    }
    this.toggleLocationSelection(location);
  }

  isLocationScopeSelected(locationID: number): boolean {
    return this.selectedLocationScopes.some(scope => scope.LocationID === locationID);
  }

  toggleLocationSelection(location: AuditLocation, event?: boolean | Event) {
    if (this.readOnlyMode || !location.HasPermission) {
      return;
    }
    const checked = typeof event === 'boolean'
      ? event
      : event
        ? (event.target as HTMLInputElement).checked
        : !this.isLocationScopeSelected(location.LocationID);
    if (!checked) {
      this.removeLocationSelection(location.LocationID);
      return;
    }
    this.selectedLocation = location;
    this.isLocationSelected = true;
    const existing = this.selectedLocationScopes.find(scope => scope.LocationID === location.LocationID);
    if (existing) {
      existing.IncludeSubLocations = this.includeSubLocations;
    } else {
      this.selectedLocationScopes.push({
        LocationID: location.LocationID,
        LocationName: location.LocationName,
        IncludeSubLocations: this.includeSubLocations,
        LocationLevelSeq: location.LocationLevelSeq
      });
    }
    this.scheduleLocationSave();
    this.buildLocationScope();
  }

  updateIncludeSubLocations(value: boolean) {
    if (this.readOnlyMode) {
      return;
    }
    this.includeSubLocations = value;
    if (this.selectedLocationScopes.length === 0) {
      return;
    }
    this.selectedLocationScopes.forEach(scope => {
      scope.IncludeSubLocations = value;
    });
    this.buildLocationScope();
    this.scheduleLocationSave();
  }

  locationHasChildren(location: AuditLocation) {
    return this.originalLocations.findIndex((x) => x.ParentLocationID === location.LocationID) !== -1;
  }

  buildLocationDDL(locationID: number) {
    if (this.readOnlyMode) {
      return;
    }
    this.topLevel = false;
    const auditLocations: AuditLocation[] = [];
    this.originalLocations.forEach((location) => {
      if (location.ParentLocationID === locationID) {
        auditLocations.push(location);
      }
    });
    this.possibleLocations = auditLocations;
  }

  resetLocations() {
    if (this.readOnlyMode) {
      return;
    }
    this.initialList();
    this.isLocationSelected = false;
    this.selectedLocation = new AuditLocation();
  }

  nextStep() {
    if (this.readOnlyMode) {
      if (this.currentStep === 2 && !this.permissionsReady && this.createdUser) {
        this.loadExistingPermissionsForEdit(this.createdUser.UserID);
      }
      this.currentStep = Math.min(this.currentStep + 1, 4);
      if (this.currentStep === 4) {
        this.loadUserAudits();
      }
      return;
    }
    if (this.currentStep === 1) {
      if (this.createdUser) {
        this.saveUserDetails();
        return;
      }
      this.validateUser();
      return;
    }
    if (this.currentStep === 2) {
      this.confirmLocation();
      return;
    }
    if (this.currentStep === 3) {
      this.savePermissionsAndNext();
      return;
    }
    this.currentStep = Math.min(this.currentStep + 1, 4);
  }

  prevStep() {
    if (this.currentStep === 2) {
      this.restoreUserDetails();
      this.isSavingUser = false;
      this.isSavingLocations = false;
      this.isSavingPermissions = false;
    }
    this.currentStep = Math.max(this.currentStep - 1, 1);
  }

  validateUser() {
    this.resetFlags();
    let isValid = true;
    if (this.newUser.Username.length < this.minUserLength) {
      this.invalidUserName = true;
      isValid = false;
    }
    if (this.noLicense) {
      isValid = false;
    }
    if (this.hasWhiteSpace(this.newUser.Username)) {
      this.invalidUserName = true;
      isValid = false;
    }
    if (!this.validEmail(this.newUser.Email)) {
      this.invalidEmail = true;
      isValid = false;
    }
    if (this.newUser.DisplayName.length < this.minUserLength) {
      this.invalidDisplayName = true;
      isValid = false;
    }
    if (isValid) {
      this.saveUser();
    }
  }

  saveUser() {
    if (this.createdUser) {
      return;
    }
    this.isSavingUser = true;
    this.UMS.newUser(this.newUser)
      .pipe(tap((result: NewUserResult) => this.processUserSave(result)))
      .subscribe();
  }

  processUserSave(userResult: NewUserResult) {
    this.isSavingUser = false;
    if (userResult.IsError === false) {
      this.createdUser = new SaveUser(userResult.UserDetails);
      this.newUser.UserID = this.createdUser.UserID;
      this.newUser.UserTypeID = this.createdUser.UserTypeID;
      this.cacheUserDetails();
      this.toast.addToastr(new Toastr({ Type: 2, Message: "User Saved." }));
      this.currentStep = 2;
      return;
    }

    if (!userResult.IsLicenseAvailable) {
      this.noLicense = true;
    }
    if (userResult.IsUsernameInUse) {
      this.isUsernameInUse = true;
    }
    if (!userResult.IsEmailValid) {
      this.invalidEmail = true;
    }
    if (userResult.IsEmailInUse) {
      this.emailUsed = true;
    }
    if (!userResult.IsUsernameValid) {
      this.invalidUserName = true;
    }
  }

  confirmLocation() {
    if (this.isSavingLocations) {
      return;
    }
    if (this.selectedLocationScopes.length === 0) {
      return;
    }
    this.buildLocationScope();
    const request = new UserPermissionsLocationListRequest({
      IsGroup: false,
      LocationIDs: this.getSelectedLocationIds()
    });
    this.permissionsReady = false;
    this.isSavingLocations = true;
    this.resolveUserId()
      .pipe(
        switchMap((resolved) => {
          if (!resolved || !this.createdUser) {
            this.isSavingLocations = false;
            this.toast.addToastr(new Toastr({ Message: "Could not resolve user ID for locations.", Type: 3 }));
            return of(null);
          }
          return this.saveUserLocations();
        }),
        switchMap((locationResult) => {
          if (!locationResult) {
            this.isSavingLocations = false;
            return of(null);
          }
          return this.UMS.userManagementListByLocations(request);
        }),
        tap((result: UserManagementResult | null) => {
          this.isSavingLocations = false;
          if (!result) {
            return;
          }
          this.setMirrorUsers(result);
          if (this.newUserLocations.length === 0) {
            this.buildLocationScope();
          }
          this.permissionsReady = true;
          this.currentStep = 3;
        })
      )
      .subscribe();
  }

  saveUserDetails() {
    if (!this.createdUser) {
      return;
    }
    if (!this.hasUserDetailsChanged()) {
      this.currentStep = 2;
      return;
    }
    this.isSavingUser = true;
    this.resolveUserId()
      .pipe(
        switchMap((resolved) => {
          if (!resolved) {
            return of(false);
          }
          return this.checkManagedUserEmailBeforeSave();
        }),
        switchMap((resolved) => {
          if (!resolved || !this.createdUser) {
            this.isSavingUser = false;
            return of(null);
          }
          this.newUser.UserID = this.createdUser.UserID;
          this.newUser.UserTypeID = this.createdUser.UserTypeID;
          return this.UMS.saveManageUsers(this.newUser);
        }),
        tap((result) => {
          if (!result) {
            return;
          }
          if (result.IsError) {
            if (result.ErrorCode === "SMU006") {
              this.reactivationLicenseUnavailable = true;
              this.newUser.Active = false;
              this.detailsDirty = false;
              this.toast.addToastr(new Toastr({ Message: "This user cannot be reactivated because there are no available user licences. Please contact your account manager.", Type: 3 }));
              return;
            }
            if (result.ErrorCode === "SMU005") {
              this.emailUsed = true;
              this.toast.addToastr(new Toastr({ Message: "Email is already assigned to an account.", Type: 3 }));
              return;
            }
            if (result.ErrorCode === "SMU003") {
              this.currentStep = 2;
              return;
            }
            this.toast.addToastr(new Toastr({ Message: "Error saving user details.", Type: 3 }));
            return;
          }
          this.cacheUserDetails();
          this.userDetailsSaved.emit(new SaveUser(this.newUser));
          this.toast.addToastr(new Toastr({ Type: 2, Message: "User details updated." }));
          this.currentStep = 2;
        }),
        catchError(() => {
          this.toast.addToastr(new Toastr({ Message: "Error saving user details.", Type: 3 }));
          return EMPTY;
        }),
        finalize(() => {
          this.isSavingUser = false;
        })
      )
      .subscribe();
  }

  hasUserDetailsChanged() {
    if (!this.userDetailsDraft) {
      return true;
    }
    return (
      this.newUser.Username !== this.userDetailsDraft.Username ||
      this.newUser.DisplayName !== this.userDetailsDraft.DisplayName ||
      this.newUser.Email !== this.userDetailsDraft.Email ||
      this.newUser.DailyReminders !== this.userDetailsDraft.DailyReminders ||
      this.newUser.WeeklySummary !== this.userDetailsDraft.WeeklySummary ||
      this.newUser.SendDailyRecaps !== this.userDetailsDraft.SendDailyRecaps ||
      this.newUser.SendActionEmails !== this.userDetailsDraft.SendActionEmails ||
      this.newUser.Active !== this.userDetailsDraft.Active ||
      this.newUser.UserTypeID !== this.userDetailsDraft.UserTypeID
    );
  }

  cacheUserDetails() {
    this.userDetailsDraft = new SaveUser({ ...this.newUser });
  }

  restoreUserDetails() {
    if (!this.userDetailsDraft) {
      return;
    }
    this.newUser = new SaveUser({ ...this.userDetailsDraft });
  }

  buildLocationScope() {
    const locationIds = this.getSelectedLocationIds();
    if (locationIds.length === 0) {
      return;
    }
    const scopedLocations = this.originalLocations
      .filter((location) => locationIds.includes(location.LocationID))
      .map((location) => {
        const clone = new AuditLocation(location);
        clone.HasPermission = true;
        clone.Features = new FeaturesList();
        clone.SubLocationFeatures = new FeaturesList();
        return clone;
      })
      .sort((a, b) => {
        if (a.LocationLevelSeq === b.LocationLevelSeq) {
          return a.LocationName.localeCompare(b.LocationName);
        }
        return a.LocationLevelSeq - b.LocationLevelSeq;
      });
    if (scopedLocations.length === 0) {
      if (this.editMode && this.newUserLocations.length > 0) {
        return;
      }
      this.newUserLocations = this.selectedLocationScopes.map(scope => new AuditLocation({
        LocationID: scope.LocationID,
        LocationName: scope.LocationName,
        LocationLevelSeq: scope.LocationLevelSeq,
        HasPermission: true
      }));
      this.mobileSelectedLocationId = null;
      return;
    }
    this.newUserLocations = scopedLocations;
    this.mobileSelectedLocationId = null;
  }

  getSubLocationIDs(locationID: number): number[] {
    const subLocationIDs: number[] = [];
    this.originalLocations.forEach((location) => {
      if (location.ParentLocationID === locationID) {
        subLocationIDs.push(location.LocationID);
        subLocationIDs.push(...this.getSubLocationIDs(location.LocationID));
      }
    });
    return subLocationIDs;
  }

  applyMirrorPermissions() {
    if (this.readOnlyMode) {
      return;
    }
    if (this.mirrorUserId == null) {
      return;
    }
    const mirrorUser = this.mirrorUsers.find((user) => user.UserID === this.mirrorUserId);
    if (!mirrorUser) {
      return;
    }
    this.newUserLocations.forEach((location) => {
      const mirrorLocation = mirrorUser.Locations?.find((loc) => loc.LocationID === location.LocationID);
      if (mirrorLocation) {
        location.Features = new FeaturesList(mirrorLocation.Features);
        location.SubLocationFeatures = new FeaturesList(mirrorLocation.SubLocationFeatures);
      } else {
        location.Features = new FeaturesList();
        location.SubLocationFeatures = new FeaturesList();
      }
    });
    this.syncSimpleFromAdvancedAll();
    this.schedulePermissionsSave();
  }

  private setMirrorUsers(result: UserManagementResult | null) {
    const currentUserId = this.createdUser?.UserID ?? -1;
    this.mirrorUsers = (result?.Users ?? [])
      .filter((user) => user.UserID !== currentUserId)
      .sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));

    if (this.mirrorUserId != null && !this.mirrorUsers.some((user) => user.UserID === this.mirrorUserId)) {
      this.mirrorUserId = null;
    }
  }

  clearPermissions() {
    if (this.readOnlyMode) {
      return;
    }
    this.newUserLocations.forEach((location) => {
      location.Features = new FeaturesList();
      location.SubLocationFeatures = new FeaturesList();
    });
    this.schedulePermissionsSave();
  }

  get mobileSelectedLocation(): AuditLocation | null {
    if (this.mobileSelectedLocationId == null) {
      return null;
    }
    return this.newUserLocations.find((location) => location.LocationID === this.mobileSelectedLocationId) ?? null;
  }

  selectMobileLocation(locationID: number) {
    this.mobileSelectedLocationId = locationID;
  }

  clearMobileLocationSelection() {
    this.mobileSelectedLocationId = null;
  }

  updateSimplePermission(locationID: number, featureType: number, value: boolean) {
    if (this.readOnlyMode) {
      return;
    }
    const location = this.newUserLocations.find((loc) => loc.LocationID === locationID);
    if (!location) {
      return;
    }
    switch (featureType) {
      case 1:
        location.Features.SimpleSaveAudit = value;
        location.Features.SimpleViewAudit = value || location.Features.SimpleViewAudit;
        break;
      case 2:
        location.Features.SimpleViewAudit = value;
        break;
      case 3:
        location.Features.SimpleCompleteAction = value;
        location.Features.SimpleViewAction = value || location.Features.SimpleViewAction;
        break;
      case 4:
        location.Features.SimpleViewAction = value;
        break;
      case 5:
        location.Features.SimpleAdmin = value;
        break;
    }
    if (!this.useAdvancedPermissions) {
      this.syncAdvancedFromSimple(location);
    }
    this.schedulePermissionsSave();
  }

  updateAdvancedPermission(locationID: number, field: keyof FeaturesList, value: boolean) {
    if (this.readOnlyMode) {
      return;
    }
    const location = this.newUserLocations.find((loc) => loc.LocationID === locationID);
    if (!location) {
      return;
    }
    location.Features[field] = value as never;
    this.syncSimpleFromAdvanced(location);
    this.schedulePermissionsSave();
  }

  syncAdvancedFromSimple(location: AuditLocation) {
    location.Features.ViewAudit = location.Features.SimpleViewAudit || location.Features.SimpleSaveAudit;
    location.Features.SaveAudit = location.Features.SimpleSaveAudit;
    location.Features.CompleteAction = location.Features.SimpleCompleteAction;
    location.Features.ViewAction = location.Features.SimpleViewAction || location.Features.SimpleCompleteAction;
    location.Features.EditUsers = location.Features.SimpleAdmin;
    location.Features.EditLocations = location.Features.SimpleAdmin;
    location.Features.GroupManagement = location.Features.SimpleAdmin;
  }

  syncSimpleFromAdvanced(location: AuditLocation) {
    location.Features.SimpleSaveAudit = location.Features.SaveAudit;
    location.Features.SimpleViewAudit = location.Features.ViewAudit || location.Features.SaveAudit;
    location.Features.SimpleCompleteAction = location.Features.CompleteAction;
    location.Features.SimpleViewAction = location.Features.ViewAction || location.Features.CompleteAction;
    location.Features.SimpleAdmin = location.Features.EditUsers || location.Features.EditLocations || location.Features.GroupManagement;
  }

  syncSimpleFromAdvancedAll() {
    this.newUserLocations.forEach((location) => this.syncSimpleFromAdvanced(location));
  }

  loadUserAudits() {
    if (this.auditLoaded || this.auditLoading) {
      return;
    }
    this.auditLoading = true;
    this.resolveUserId()
      .pipe(
        switchMap((resolved) => {
          if (!resolved || !this.createdUser) {
            this.auditLoading = false;
            return of(null);
          }
          return this.UMS.getIndividualUserAudits(this.createdUser.UserID);
        }),
        tap((result: UserAuditResult | null) => {
          this.auditLoading = false;
          if (!result || result.IsError) {
            return;
          }
          if (result.Users.length > 0) {
            this.applyAuditPermissions(result.Users[0]);
            return;
          }
          this.userAudits = new UserAudit({ UserID: this.createdUser?.UserID ?? -1 });
          this.originalUserAudits = new UserAudit(this.userAudits);
          this.updateAuditTypes();
          this.auditLoaded = true;
        })
      )
      .subscribe();
  }

  applyAuditPermissions(userAudit: UserAudit) {
    this.originalUserAudits = new UserAudit(userAudit);
    this.userAudits = new UserAudit(userAudit);
    this.updateAuditTypes();
    this.auditLoaded = true;
    this.checkAllAuditPermissions(true);
  }

  updateAllAuditPermissions() {
    this.userAudits.AuditPermissions.forEach((permission) => {
      permission.HasPermission = this.allAuditPermissions;
    });
    this.checkAllAuditPermissions();
  }

  checkAllAuditPermissions(skipSave: boolean = false) {
    this.allAuditPermissions = this.userAudits.AuditPermissions.every((permission) => permission.HasPermission);
    if (!skipSave) {
      this.scheduleAuditsSave();
    }
  }

  updateAuditTypes() {
    const typeMap = new Map<number, string>();
    this.userAudits.AuditPermissions.forEach((audit) => {
      const typeId = audit.AuditTypeID ?? -1;
      const typeName = audit.AuditType || 'Uncategorized';
      if (!typeMap.has(typeId)) {
        typeMap.set(typeId, typeName);
      }
    });
    this.auditTypes = Array.from(typeMap.entries())
      .map(([AuditTypeID, AuditType]) => ({ AuditTypeID, AuditType }))
      .sort((a, b) => a.AuditType.localeCompare(b.AuditType));
  }

  isAuditTypeSelected(auditTypeID: number) {
    const audits = this.userAudits.AuditPermissions.filter((audit) => audit.AuditTypeID === auditTypeID);
    return audits.length > 0 && audits.every((audit) => audit.HasPermission);
  }

  toggleAuditType(auditTypeID: number, value: boolean) {
    this.userAudits.AuditPermissions.forEach((audit) => {
      if (audit.AuditTypeID === auditTypeID) {
        audit.HasPermission = value;
      }
    });
    this.checkAllAuditPermissions();
  }

  resetAuditPermissions() {
    this.userAudits = new UserAudit(this.originalUserAudits);
    this.checkAllAuditPermissions();
  }

  saveUserAudits() {
    if (!this.userAudits || this.userAudits.AuditPermissions.length === 0) {
      return of(new UserAuditResult());
    }
    return this.UMS.saveIndividualUserAudits(this.userAudits).pipe(
      map((result) => {
        if (result.IsError) {
          this.toast.addToastr(new Toastr({ Message: "Error saving user audits.", Type: 3 }));
          return null;
        }
        if (result.Users.length > 0) {
          this.applyAuditPermissions(result.Users[0]);
        }
        return result;
      })
    );
  }

  saveUserLocations() {
    if (!this.createdUser) {
      return of(null);
    }
    const selectedIds = new Set<number>(this.getSelectedLocationIds());
    const locationMap = new Map<number, AuditLocation>();
    this.originalLocations.forEach((location) => {
      locationMap.set(location.LocationID, location);
    });
    return this.UMS.getUserLocationLocations(this.createdUser.UserID).pipe(
      switchMap((locations) => {
        const updated = locations.map((location) => {
          const clone = new AuditLocation(location);
          const fallback = locationMap.get(clone.LocationID);
          if (clone.LocationLevelID < 0) {
            clone.LocationLevelID = fallback?.LocationLevelID ?? 0;
          }
          if (clone.LocationLevelSeq < 0) {
            clone.LocationLevelSeq = fallback?.LocationLevelSeq ?? 0;
          }
          if (clone.ParentLocationID < 0) {
            clone.ParentLocationID = fallback?.ParentLocationID ?? 0;
          }
          if (clone.Seq < 0) {
            clone.Seq = fallback?.Seq ?? 0;
          }
          if (!clone.LocationName && fallback?.LocationName) {
            clone.LocationName = fallback.LocationName;
          }
          if (fallback && clone.Active === undefined) {
            clone.Active = fallback.Active;
          }
          clone.HasPermission = selectedIds.has(clone.LocationID);
          return clone;
        });
        return this.UMS.saveUserLocationLocations(new UserLocationLocationSave({
          UserID: this.createdUser!.UserID,
          IsGroup: false,
          Locations: updated
        }));
      }),
      map((result) => {
        if (result.IsError) {
          this.toast.addToastr(new Toastr({ Message: "Error saving user locations.", Type: 3 }));
          return null;
        }
        return result;
      })
    );
  }

  saveWizard() {
    if (this.readOnlyMode) {
      this.exitWizard();
      return;
    }
    if (!this.createdUser) {
      return;
    }
    this.isSavingPermissions = true;
    this.resolveUserId()
      .pipe(
        switchMap((resolved) => {
          if (!resolved || !this.createdUser) {
            this.isSavingPermissions = false;
            this.toast.addToastr(new Toastr({ Message: "Could not resolve user ID for audits.", Type: 3 }));
            return of(null);
          }
          return this.saveUserAudits();
        }),
        tap((result: UserAuditResult | null) => {
          this.isSavingPermissions = false;
          if (!result) {
            return;
          }
          if (result.IsError) {
            this.toast.addToastr(new Toastr({ Message: "Error saving user audits.", Type: 3 }));
            return;
          }
          this.toast.addToastr(new Toastr({ Message: "User created and audits saved.", Type: 2 }));
          this.router.navigateByUrl("/settings");
        })
      )
      .subscribe();
  }

  savePermissionsAndNext() {
    if (this.readOnlyMode) {
      this.currentStep = 4;
      this.loadUserAudits();
      return;
    }
    if (this.isSavingPermissions) {
      return;
    }
    if (!this.hasAnyPermissionSelected()) {
      this.toast.addToastr(new Toastr({ Message: "Select at least one permission.", Type: 3 }));
      return;
    }
    const payload = this.buildPermissionsPayload();
    if (!payload) {
      return;
    }
    this.isSavingPermissions = true;
    this.resolveUserId()
      .pipe(
        switchMap((resolved) => {
          if (!resolved || !this.createdUser) {
            this.isSavingPermissions = false;
            this.toast.addToastr(new Toastr({ Message: "Could not resolve user ID for permissions.", Type: 3 }));
            return of(null);
          }
          return this.UMS.saveUserManagementList(payload);
        }),
        tap((result: UserManagementResult | null) => {
          this.isSavingPermissions = false;
          if (!result) {
            return;
          }
          if (result.IsError) {
            this.toast.addToastr(new Toastr({ Message: "Error saving permissions.", Type: 3 }));
            return;
          }
          if (this.editMode && this.createdUser) {
            this.refreshLocationsAfterPermissionsSave(this.createdUser.UserID);
          }
          this.toast.addToastr(new Toastr({ Message: "Permissions saved.", Type: 2 }));
          this.currentStep = 4;
          this.loadUserAudits();
        })
      )
      .subscribe();
  }

  private refreshLocationsAfterPermissionsSave(userId: number) {
    this.UMS.getUserLocationLocations(userId)
      .pipe(
        tap((locations: AuditLocation[]) => {
          const assigned = locations.filter(l => l.HasPermission);
          this.selectedLocationScopes = assigned.map(l => ({
            LocationID: l.LocationID,
            LocationName: l.LocationName,
            IncludeSubLocations: false,
            LocationLevelSeq: l.LocationLevelSeq
          }));
        })
      )
      .subscribe();
  }

  buildPermissionsPayload(): UserManagementResult | null {
    const createdUser = this.createdUser;
    if (!createdUser) {
      return null;
    }
    const scopedLocationIds = this.getPermissionSaveLocationIds();
    if (scopedLocationIds.length === 0) {
      return null;
    }
    const returnData = new UserManagementResult();
    returnData.UPR = new UserPermissionsRequest({
      IsGroup: false,
      IncludeSublocations: false,
      LocationID: -1,
      FullLocationList: scopedLocationIds
    });
    returnData.IsGroup = false;
    returnData.UseSimplePermissions = !this.useAdvancedPermissions;
    const permUser = new PermissionsUser(createdUser);
    permUser.Locations = this.newUserLocations.filter((location) => scopedLocationIds.includes(location.LocationID));
    returnData.Users = [permUser];
    return returnData;
  }

  private getPermissionSaveLocationIds(): number[] {
    const locationIds = new Set<number>(this.getSelectedLocationIds());
    this.newUserLocations
      .filter((location) => this.locationHasAnyPermission(location))
      .forEach((location) => locationIds.add(location.LocationID));
    return Array.from(locationIds);
  }

  private locationHasAnyPermission(location: AuditLocation): boolean {
    const f = location.Features;
    return (
      f.ViewAudit ||
      f.SaveAudit ||
      f.EditAudit ||
      f.EditLocations ||
      f.EditUsers ||
      f.ScheduleAudit ||
      f.DeleteScheduledAudit ||
      f.CompleteAction ||
      f.EditAction ||
      f.ViewAction ||
      f.GroupManagement ||
      f.ViewCampaignResults ||
      f.EditCampaigns ||
      f.Reporting ||
      f.SimpleSaveAudit ||
      f.SimpleViewAudit ||
      f.SimpleCompleteAction ||
      f.SimpleViewAction ||
      f.SimpleAdmin
    );
  }

  hasAnyPermissionSelected() {
    if (!this.newUserLocations || this.newUserLocations.length === 0) {
      return false;
    }
    if (this.useAdvancedPermissions) {
      return this.newUserLocations.some((location) => {
        const f = location.Features;
        return (
          f.ViewAudit ||
          f.SaveAudit ||
          f.EditAudit ||
          f.EditLocations ||
          f.EditUsers ||
          f.ScheduleAudit ||
          f.DeleteScheduledAudit ||
          f.CompleteAction ||
          f.EditAction ||
          f.ViewAction ||
          f.GroupManagement ||
          f.ViewCampaignResults ||
          f.EditCampaigns ||
          f.Reporting
        );
      });
    }
    return this.newUserLocations.some((location) => {
      const f = location.Features;
      return (
        f.SimpleSaveAudit ||
        f.SimpleViewAudit ||
        f.SimpleCompleteAction ||
        f.SimpleViewAction ||
        f.SimpleAdmin
      );
    });
  }

  resolveUserId() {
    if (this.createdUser && this.createdUser.UserID > 0) {
      return of(true);
    }
    if (!this.newUser.Username) {
      return of(false);
    }
    return this.UMS.getManageUsers(false).pipe(
      map((users) => {
        const match = users.find((user) => user.Username?.toLowerCase() === this.newUser.Username.toLowerCase());
        if (!match) {
          return false;
        }
        this.createdUser = new SaveUser(match);
        this.newUser.UserID = match.UserID;
        this.newUser.UserTypeID = match.UserTypeID;
        this.cacheUserDetails();
        return true;
      })
    );
  }

  resetFlags() {
    this.invalidEmail = false;
    this.invalidUserName = false;
    this.invalidDisplayName = false;
    this.isUsernameInUse = false;
  }

  resetEmailFlags() {
    this.invalidEmail = false;
    this.emailUsed = false;
    this.scheduleDetailsSave();
  }

  private checkManagedUserEmailConflict(showWarning: boolean) {
    if (!this.editMode || !this.createdUser || !this.newUser.Email) {
      return;
    }
    this.UMS.checkManagedUserEmail(this.newUser)
      .pipe(
        tap((result: BasicResult) => {
          this.emailUsed = result.IsError && result.ErrorCode === "SMU005";
          this.invalidEmail = result.IsError && result.ErrorCode === "SMU004";
          if (showWarning && this.emailUsed) {
            this.toast.addToastr(new Toastr({ Message: "This inactive user's email is already assigned to an active account. Update the email before making further changes.", Type: 3 }));
          }
        }),
        catchError(() => EMPTY)
      )
      .subscribe();
  }

  private checkManagedUserEmailBeforeSave() {
    if (!this.editMode || !this.createdUser) {
      return of(true);
    }
    if (!this.validEmail(this.newUser.Email)) {
      this.invalidEmail = true;
      this.toast.addToastr(new Toastr({ Message: "Email is invalid.", Type: 3 }));
      return of(false);
    }
    return this.UMS.checkManagedUserEmail(this.newUser)
      .pipe(
        map((result: BasicResult) => {
          this.emailUsed = result.IsError && result.ErrorCode === "SMU005";
          this.invalidEmail = result.IsError && result.ErrorCode === "SMU004";
          if (this.emailUsed) {
            this.toast.addToastr(new Toastr({ Message: "This email is already assigned to an active account. Update the email before making further changes.", Type: 3 }));
            return false;
          }
          if (this.invalidEmail) {
            this.toast.addToastr(new Toastr({ Message: "Email is invalid.", Type: 3 }));
            return false;
          }
          return !result.IsError;
        }),
        catchError(() => {
          this.toast.addToastr(new Toastr({ Message: "Unable to check the email address, please try again.", Type: 3 }));
          return of(false);
        })
      );
  }

  validEmail(email: string) {
    const reg = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    return reg.test(email);
  }

  hasWhiteSpace(value: string) {
    return value.indexOf(' ') >= 0;
  }

  goBack() {
    this.router.navigateByUrl("/settings");
  }

  removeLocationSelection(locationId: number) {
    if (this.readOnlyMode) {
      return;
    }
    this.selectedLocationScopes = this.selectedLocationScopes.filter(s => s.LocationID !== locationId);
    this.isLocationSelected = this.selectedLocationScopes.length > 0;
    this.selectedLocation = this.selectedLocationScopes.length > 0
      ? new AuditLocation(this.selectedLocationScopes[this.selectedLocationScopes.length - 1])
      : new AuditLocation();
    this.buildLocationScope();
    this.scheduleLocationSave();
  }

  hasAnyLocationSelection(): boolean {
    return this.selectedLocationScopes.length > 0;
  }

  private getSelectedLocationIds(): number[] {
    const locationIds = new Set<number>();
    this.selectedLocationScopes.forEach(scope => {
      locationIds.add(scope.LocationID);
      if (scope.IncludeSubLocations) {
        this.getSubLocationIDs(scope.LocationID).forEach(id => locationIds.add(id));
      }
    });
    return Array.from(locationIds);
  }

  private exitWizard() {
    this.router.navigateByUrl("/settings");
  }

  scheduleDetailsSave() {
    if (!this.editMode || this.readOnlyMode) {
      return;
    }
    this.detailsDirty = true;
    this.detailsSave$.next();
  }

  scheduleLocationSave() {
    if (!this.editMode || this.readOnlyMode) {
      return;
    }
    this.locationsDirty = true;
    this.permissionsReady = false;
    this.mirrorUsers = [];
    this.mirrorUserId = null;
    this.locationsSave$.next();
  }

  schedulePermissionsSave() {
    if (!this.editMode || this.readOnlyMode) {
      return;
    }
    this.permissionsDirty = true;
    this.permissionsSave$.next();
  }

  scheduleAuditsSave() {
    if (!this.editMode || this.readOnlyMode) {
      return;
    }
    this.auditsDirty = true;
    this.auditsSave$.next();
  }

  flushPendingSaves(step: number) {
    if (!this.editMode || this.readOnlyMode) {
      return;
    }
    switch (step) {
      case 1:
        this.trySaveDetails();
        break;
      case 2:
        this.trySaveLocations();
        break;
      case 3:
        this.trySavePermissions();
        break;
      case 4:
        this.trySaveAudits();
        break;
    }
  }

  onTabActivated(step: number) {
    if (!this.editMode) {
      return;
    }
    if (step === 3 && !this.permissionsReady && this.createdUser) {
      this.loadExistingPermissionsForEdit(this.createdUser.UserID);
    }
    if (step === 4) {
      this.loadUserAudits();
    }
  }

  public isLoadingForStep(step: number): boolean {
    if (!this.editMode) {
      return false;
    }
    switch (step) {
      case 2:
        return this.isLoadingLocationsForEdit;
      case 3:
        return this.isLoadingPermissionsForEdit;
      case 4:
        return this.auditLoading;
      default:
        return false;
    }
  }

  private trySaveDetails() {
    if (!this.detailsDirty || this.isSavingUser) {
      return;
    }
    if (!this.createdUser || !this.hasUserDetailsChanged()) {
      this.detailsDirty = false;
      return;
    }
    this.isSavingUser = true;
    this.resolveUserId()
      .pipe(
        switchMap((resolved) => {
          if (!resolved) {
            return of(false);
          }
          return this.checkManagedUserEmailBeforeSave();
        }),
        switchMap((resolved) => {
          if (!resolved || !this.createdUser) {
            return of(null);
          }
          this.newUser.UserID = this.createdUser.UserID;
          this.newUser.UserTypeID = this.createdUser.UserTypeID;
          return this.UMS.saveManageUsers(this.newUser);
        }),
        tap((result) => {
          if (!result) {
            return;
          }
          if (result.IsError) {
            if (result.ErrorCode === "SMU006") {
              this.reactivationLicenseUnavailable = true;
              this.newUser.Active = false;
              this.detailsDirty = false;
              this.toast.addToastr(new Toastr({ Message: "This user cannot be reactivated because there are no available user licences. Please contact your account manager.", Type: 3 }));
              return;
            }
            if (result.ErrorCode === "SMU005") {
              this.emailUsed = true;
              this.toast.addToastr(new Toastr({ Message: "Email is already assigned to an account.", Type: 3 }));
              return;
            }
            this.toast.addToastr(new Toastr({ Message: "Error saving user details.", Type: 3 }));
            return;
          }
          this.cacheUserDetails();
          this.detailsDirty = false;
          this.userDetailsSaved.emit(new SaveUser(this.newUser));
          this.toast.addToastr(new Toastr({ Type: 2, Message: "User details saved." }));
        }),
        catchError(() => {
          this.toast.addToastr(new Toastr({ Message: "Error saving user details.", Type: 3 }));
          return EMPTY;
        }),
        finalize(() => {
          this.isSavingUser = false;
        })
      )
      .subscribe();
  }

  private trySaveLocations() {
    if (!this.locationsDirty || this.isSavingLocations) {
      return;
    }
    if (!this.createdUser || this.getSelectedLocationIds().length === 0) {
      this.locationsDirty = false;
      return;
    }
    this.isSavingLocations = true;
    this.resolveUserId()
      .pipe(
        switchMap((resolved) => {
          if (!resolved || !this.createdUser) {
            this.toast.addToastr(new Toastr({ Message: "Could not resolve user ID for locations.", Type: 3 }));
            return of(null);
          }
          return this.saveUserLocations();
        }),
        tap((result) => {
          if (!result) {
            return;
          }
          this.locationsDirty = false;
          this.buildLocationScope();
          if (this.createdUser) {
            this.loadExistingPermissionsForEdit(this.createdUser.UserID);
          }
          this.toast.addToastr(new Toastr({ Type: 2, Message: "Locations saved." }));
        }),
        catchError(() => {
          this.toast.addToastr(new Toastr({ Message: "Error saving user locations.", Type: 3 }));
          return EMPTY;
        }),
        finalize(() => {
          this.isSavingLocations = false;
        })
      )
      .subscribe();
  }

  private trySavePermissions() {
    if (!this.permissionsDirty || this.isSavingPermissions) {
      return;
    }
    const payload = this.buildPermissionsPayload();
    if (!payload || !this.createdUser) {
      this.permissionsDirty = false;
      return;
    }
    this.isSavingPermissions = true;
    this.resolveUserId()
      .pipe(
        switchMap((resolved) => {
          if (!resolved || !this.createdUser) {
            this.toast.addToastr(new Toastr({ Message: "Could not resolve user ID for permissions.", Type: 3 }));
            return of(null);
          }
          return this.saveUserLocations()
            .pipe(
              switchMap((locationResult) => {
                if (!locationResult) {
                  return of(null);
                }
                return this.UMS.saveUserManagementList(payload);
              })
            );
        }),
        tap((result) => {
          if (!result) {
            return;
          }
          if (result.IsError) {
            this.toast.addToastr(new Toastr({ Message: "Error saving permissions.", Type: 3 }));
            return;
          }
          this.permissionsDirty = false;
          if (this.createdUser) {
            this.refreshLocationsAfterPermissionsSave(this.createdUser.UserID);
          }
          this.toast.addToastr(new Toastr({ Message: "Permissions saved.", Type: 2 }));
        }),
        catchError(() => {
          this.toast.addToastr(new Toastr({ Message: "Error saving permissions.", Type: 3 }));
          return EMPTY;
        }),
        finalize(() => {
          this.isSavingPermissions = false;
        })
      )
      .subscribe();
  }

  private trySaveAudits() {
    if (!this.auditsDirty || this.isSavingPermissions) {
      return;
    }
    if (!this.createdUser) {
      this.auditsDirty = false;
      return;
    }
    this.isSavingPermissions = true;
    this.resolveUserId()
      .pipe(
        switchMap((resolved) => {
          if (!resolved || !this.createdUser) {
            this.toast.addToastr(new Toastr({ Message: "Could not resolve user ID for audits.", Type: 3 }));
            return of(null);
          }
          return this.saveUserAudits();
        }),
        tap((result) => {
          if (!result) {
            return;
          }
          if (result.IsError) {
            this.toast.addToastr(new Toastr({ Message: "Error saving user audits.", Type: 3 }));
            return;
          }
          this.auditsDirty = false;
          this.toast.addToastr(new Toastr({ Message: "Audit access saved.", Type: 2 }));
        }),
        catchError(() => {
          this.toast.addToastr(new Toastr({ Message: "Error saving user audits.", Type: 3 }));
          return EMPTY;
        }),
        finalize(() => {
          this.isSavingPermissions = false;
        })
      )
      .subscribe();
  }

  checkForExternalChanges(step: number) {
    if (!this.editMode || !this.createdUser) {
      return;
    }
    if (this.isSavingUser || this.isSavingLocations || this.isSavingPermissions) {
      return;
    }
    if (this.isLoadingForStep(step)) {
      return;
    }
    const userId = this.createdUser.UserID;
    switch (step) {
      case 1:
        this.UMS.getManageUsers(false)
          .pipe(
            map((users) => users.find((u) => u.UserID === userId) ?? null),
            tap((serverUser) => {
              if (!serverUser) {
                return;
              }
              if (this.hasDetailsDiff(serverUser)) {
                this.notifyExternalChange();
              } else {
                this.externalChangeDetected = false;
              }
            })
          )
          .subscribe();
        break;
      case 2:
        if (!this.locationsInitialized) {
          return;
        }
        this.UMS.getUserLocationLocations(userId)
          .pipe(
            tap((locations) => {
              const serverIds = new Set(locations.filter(l => l.HasPermission).map(l => l.LocationID));
              const localIds = new Set(this.getSelectedLocationIds());
              if (!this.areSetsEqual(serverIds, localIds)) {
                this.notifyExternalChange();
              } else {
                this.externalChangeDetected = false;
              }
            })
          )
          .subscribe();
        break;
      case 3:
        const locationIds = this.getSelectedLocationIds();
        if (!this.permissionsReady || this.newUserLocations.length === 0 || locationIds.length === 0) {
          return;
        }
        const request = new UserPermissionsLocationListRequest({
          IsGroup: false,
          LocationIDs: locationIds
        });
        this.UMS.userManagementListByLocations(request)
          .pipe(
            tap((result) => {
              const serverUser = result.Users.find(u => u.UserID === userId);
              if (!serverUser) {
                return;
              }
              if (this.hasPermissionsDiff(serverUser.Locations ?? [])) {
                this.notifyExternalChange();
              } else {
                this.externalChangeDetected = false;
              }
            })
          )
          .subscribe();
        break;
      case 4:
        this.UMS.getIndividualUserAudits(userId)
          .pipe(
            tap((result) => {
              if (!result || result.IsError || result.Users.length === 0) {
                return;
              }
              if (this.hasAuditsDiff(result.Users[0])) {
                this.notifyExternalChange();
              } else {
                this.externalChangeDetected = false;
              }
            })
          )
          .subscribe();
        break;
    }
  }

  private notifyExternalChange() {
    if (this.externalChangeDetected) {
      return;
    }
    this.externalChangeDetected = true;
  }

  private hasDetailsDiff(serverUser: SaveUser): boolean {
    const local = this.newUser;
    return (
      local.Username !== serverUser.Username ||
      local.DisplayName !== serverUser.DisplayName ||
      local.Email !== serverUser.Email ||
      local.DailyReminders !== serverUser.DailyReminders ||
      local.WeeklySummary !== serverUser.WeeklySummary ||
      local.SendDailyRecaps !== serverUser.SendDailyRecaps ||
      local.SendActionEmails !== serverUser.SendActionEmails ||
      local.Active !== serverUser.Active ||
      local.UserTypeID !== serverUser.UserTypeID
    );
  }

  private hasPermissionsDiff(serverLocations: AuditLocation[]): boolean {
    const localById = new Map<number, AuditLocation>();
    this.newUserLocations.forEach((loc) => localById.set(loc.LocationID, loc));
    for (const serverLoc of serverLocations) {
      const local = localById.get(serverLoc.LocationID);
      if (!local) {
        return true;
      }
      if (this.getFeatureSnapshot(serverLoc.Features) !== this.getFeatureSnapshot(local.Features)) {
        return true;
      }
    }
    return false;
  }

  private hasAuditsDiff(serverAudit: UserAudit): boolean {
    const localMap = new Map<number, boolean>();
    this.userAudits.AuditPermissions.forEach((a) => {
      localMap.set(a.AuditID ?? -1, a.HasPermission);
    });
    return serverAudit.AuditPermissions.some((a) => {
      const key = a.AuditID ?? -1;
      return localMap.get(key) !== a.HasPermission;
    });
  }

  private getFeatureSnapshot(features: FeaturesList | null | undefined): string {
    if (!features) {
      return '';
    }
    return [
      features.ViewAudit,
      features.SaveAudit,
      features.EditAudit,
      features.ScheduleAudit,
      features.DeleteScheduledAudit,
      features.CompleteAction,
      features.EditAction,
      features.ViewAction,
      features.EditUsers,
      features.EditLocations,
      features.GroupManagement,
      features.ViewCampaignResults,
      features.EditCampaigns,
      features.Reporting,
      features.SimpleSaveAudit,
      features.SimpleViewAudit,
      features.SimpleCompleteAction,
      features.SimpleViewAction,
      features.SimpleAdmin
    ].map(v => v ? '1' : '0').join('');
  }

  private areSetsEqual(a: Set<number>, b: Set<number>): boolean {
    if (a.size !== b.size) {
      return false;
    }
    for (const value of a) {
      if (!b.has(value)) {
        return false;
      }
    }
    return true;
  }
}
